import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Grade } from '../../types/domain';
import { parseGradeDisplayName } from '../../utils/gradeDisplayName';
import { academicYearShortLabel, classYearStatus } from '../../utils/classAcademicYear';

function ClassPillInner({
  grade,
  showGradeChips = true,
  titleMaxLength,
}: {
  grade: Grade;
  showGradeChips?: boolean;
  /** 지정 시 표시 제목을 이 글자 수로 자름(말줄임) */
  titleMaxLength?: number;
}) {
  const parsed = parseGradeDisplayName(grade.name);
  const rawTitle = parsed.title.trim();
  const truncated =
    typeof titleMaxLength === 'number' &&
    titleMaxLength > 0 &&
    rawTitle.length > titleMaxLength;
  const title = truncated ? `${rawTitle.slice(0, titleMaxLength)}…` : rawTitle;
  return (
    <>
      {grade.academic_year ? (
        <span
          className={`cp-class-year-tag${classYearStatus(grade.academic_year) !== 'active' ? ' is-locked' : ''}`}
          title={classYearStatus(grade.academic_year) !== 'active' ? '기록 중지된 지난 학년도 클래스' : undefined}
        >
          {academicYearShortLabel(grade.academic_year)}
        </span>
      ) : null}
      <span
        className="cp-grade-track-class-block-label"
        title={truncated ? rawTitle : undefined}
      >
        {title}
      </span>
      {showGradeChips ? (
        <span className="cp-sidebar-class-chip-row" aria-hidden>
          {parsed.grades.map((num) => (
            <span key={num} className="class-grade-chip-display">
              {num}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
}

export interface ClassFocusPickerProps {
  grades: Grade[];
  /** 선택된 클래스 id */
  value: string | null;
  onChange: (gradeId: string) => void;
  /** 래퍼에 추가 클래스 (예: cp-topnav-class-picker) */
  className?: string;
  /** 접근성용 라벨 */
  ariaLabel?: string;
  /**
   * 트리거 버튼에 표시할 클래스명 최대 글자 수.
   * 드롭다운 목록은 항상 전체 이름. 미지정·0 이하면 전체 표시.
   */
  buttonTitleMaxLength?: number;
}

/**
 * 사이드바·상단 네비 공통 클래스 선택 미니 모달
 */
const ClassFocusPicker: React.FC<ClassFocusPickerProps> = ({
  grades,
  value,
  onChange,
  className,
  ariaLabel = '클래스 선택',
  buttonTitleMaxLength,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selectedId = (value ?? '').trim();

  const gradesSorted = useMemo(
    () =>
      [...grades].sort((a, b) =>
        parseGradeDisplayName(a.name).title.localeCompare(
          parseGradeDisplayName(b.name).title,
          'ko',
        ),
      ),
    [grades],
  );

  const focusGrade = useMemo(
    () => (selectedId ? grades.find((g) => g.id === selectedId) ?? null : null),
    [grades, selectedId],
  );

  const focusParsed = useMemo(
    () => (focusGrade ? parseGradeDisplayName(focusGrade.name) : null),
    [focusGrade],
  );

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (gradeId: string) => {
    if (!gradeId.trim()) return;
    onChange(gradeId);
    setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={['cp-class-focus-picker', className].filter(Boolean).join(' ')}
    >
      <button
        type="button"
        className={
          'cp-class-focus-picker-btn cp-grade-track-class-block cp-sidebar-program-class-pill' +
          (open ? ' is-open' : '')
        }
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={
          focusParsed
            ? `${ariaLabel} ${focusParsed.title}, 목록 열기`
            : `${ariaLabel}, 목록 열기`
        }
        title={focusParsed ? focusParsed.title : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {/* 모바일 네비바 아이콘 모드용(기본 숨김, CSS로 표시) */}
        <span
          className="cp-class-focus-picker-icon"
          style={{
            WebkitMask: "url('/class.svg') center / contain no-repeat",
            mask: "url('/class.svg') center / contain no-repeat",
          }}
          aria-hidden
        />
        <span className="cp-sidebar-program-class-picker-btn-main">
          {focusGrade ? (
            <ClassPillInner
              grade={focusGrade}
              showGradeChips={false}
              titleMaxLength={buttonTitleMaxLength}
            />
          ) : (
            <span className="cp-sidebar-program-class-picker-placeholder">클래스를 선택하세요</span>
          )}
        </span>
        <span
          className={`cp-sidebar-program-class-picker-chevron${open ? ' is-open' : ''}`}
          aria-hidden
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <ul className="cp-sidebar-program-class-dropdown cp-class-focus-picker-dropdown" role="listbox" aria-label="등록된 클래스">
          {gradesSorted.length === 0 ? (
            <li className="cp-sidebar-program-class-dropdown-empty" role="presentation">
              등록된 클래스가 없습니다.
            </li>
          ) : (
            gradesSorted.map((g) => {
              const active = g.id === selectedId;
              return (
                <li key={g.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={
                      'cp-sidebar-program-class-dropdown-item cp-grade-track-class-block' +
                      (active ? ' is-active' : '')
                    }
                    onClick={() => pick(g.id)}
                  >
                    <ClassPillInner grade={g} />
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default ClassFocusPicker;
